import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react'

import { useBulkDeleteGraphics, useInfiniteGraphics } from '@/api/graphics'
import GraphicCard from '@/components/graphic-card'
import IconTrash from '@/components/icons/IconTrash'
import IconUpload from '@/components/icons/IconUpload'
import SelectionBar from '@/components/selection-bar'
import Button from '@/components/ui/Button'
import { H3 } from '@/components/ui/Headings'

interface CollectionGraphicsProps {
  collectionId: string
  onClickUpload: () => void
}

const MAX_GRAPHICS_FOR_DESIGN = 4

function CollectionGraphics({
  collectionId,
  onClickUpload
}: CollectionGraphicsProps) {
  const [selectedGraphics, setSelectedGraphics] = useState<Set<string>>(
    new Set()
  )
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteGraphics(collectionId)
  const bulkDeleteGraphics = useBulkDeleteGraphics()

  const isOverLimit = selectedGraphics.size > MAX_GRAPHICS_FOR_DESIGN

  const items = data?.pages.flatMap((page) => page.items) ?? []
  const total = data?.pages[0]?.count ?? 0

  const observer = useRef<IntersectionObserver | null>(null)

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return

      if (observer.current) {
        observer.current.disconnect()
      }

      if (!hasNextPage) return

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage()
        }
      })

      if (node) {
        observer.current.observe(node)
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  const handleSelect = (id: string) => {
    setSelectedGraphics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleUseGraphic = () => {
    // Design studio not yet implemented
  }

  const handleDeleteAll = async () => {
    if (!selectedGraphics.size || bulkDeleteGraphics.isPending) return

    const selectedIds = [...selectedGraphics]

    await bulkDeleteGraphics.mutateAsync(selectedIds)
    setSelectedGraphics(new Set())
  }

  const handleClearSelection = () => {
    setSelectedGraphics(new Set())
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="402px">
        <Spinner size="xl" color="accent.500" />
      </Flex>
    )
  }

  if (!items.length) {
    return (
      <Flex
        minH="402px"
        align="center"
        justify="center"
        p="24px"
      >
        <Flex direction="column" align="center" gap="24px">
          <Flex direction="column" align="center" gap="8px">
            <H3 color="text.primary" fontWeight={500}>
              You Don't Have Graphics Uploaded Yet
            </H3>
            <Text textStyle="body" color="text.secondary">
              Supported formats: PNG, JPEG
            </Text>
          </Flex>
          <Button
            variant="primary"
            w="186px"
            h="46px"
            borderRadius="8px"
            onClick={onClickUpload}
          >
            <Flex align="center" gap="8px">
              <IconUpload boxSize="20px" />
              Upload Graphics
            </Flex>
          </Button>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex direction="column" p="24px" gap={3}>
      <Text textStyle="bodySmall" color="text.secondary">
        Graphics ({total})
      </Text>

      <SimpleGrid
        columns={{ base: 2, md: 3, lg: 4, xl: 6 }}
        gap={6}
        pb={selectedGraphics.size > 0 ? '100px' : 0}
      >
        {items.map((graphic, index) => (
          <Box
            key={graphic.id}
            ref={index === items.length - 1 ? lastElementRef : undefined}
          >
            <GraphicCard
              graphic={graphic}
              isSelected={selectedGraphics.has(graphic.id)}
              onSelect={handleSelect}
              onUseGraphic={handleUseGraphic}
            />
          </Box>
        ))}
      </SimpleGrid>

      {isFetchingNextPage && (
        <Box display="flex" justifyContent="center" py={6}>
          <Spinner size="md" color="accent.500" />
        </Box>
      )}

      <SelectionBar
        selectedCount={selectedGraphics.size}
        maxCount={MAX_GRAPHICS_FOR_DESIGN}
        primaryAction={{
          label: 'Use Graphics',
          onClick: () => {},
          disabled: true,
          disabledTooltip: 'Coming soon'
        }}
        secondaryAction={{
          label: 'Delete',
          icon: <IconTrash boxSize={5} mr={2} />,
          onClick: handleDeleteAll,
          disabled: bulkDeleteGraphics.isPending || isOverLimit
        }}
        onClearSelection={handleClearSelection}
      />
    </Flex>
  )
}

export default CollectionGraphics
